<script>
import baseMixin from '@/mixins/baseMixin'
import { CreateBookingSteps } from '@/shared/constants/booking/BookingSelectEducators'
import VuetifyColourClass from '@/shared/constants/core/VuetifyColourClass'
import VuetifyTextClass from '@/shared/constants/core/VuetifyTextClass'
import { mapActions, mapGetters } from 'vuex'

export default {
  name: 'BookingCreateHeaderV2',
  mixins: [baseMixin],
  props: {
    editMode: { type: Boolean, default: false },
    bookingId: { type: [String, Number], default: null },
  },
  data() {
    return {
      ...VuetifyTextClass,
      text: VuetifyColourClass.text,
    }
  },
  computed: {
    ...mapGetters({
      isSendToANZUK: 'bookings/isSendToANZUK',
      createBookingV2Header: 'bookings/createBookingV2Header',
      createBookingV2Step: 'bookings/createBookingV2Step',
      isLoadingCRUD: 'bookings/isLoadingCRUD',
      isLoadingEditBooking: 'bookings/isLoadingEditBooking',
      isLoadingBookingBlocks: 'locations/isLoadingBookingBlocks',
    }),
    isOnDetailsStep() {
      return this.createBookingV2Step === CreateBookingSteps.DetailsStep
    },
    isOnSelectEducatorsStep() {
      return this.createBookingV2Step === CreateBookingSteps.SelectEducatorsStep
    },
    pageTitle() {
      if (this.editMode) return this.$t('bookingEdit.pageTitle')

      return this.$t('bookingCreate.breadcrumbTitle')
    },
    primaryActionLabel() {
      if (this.isOnDetailsStep && !this.isSendToANZUK) {
        return this.$t('bookingCreateV2.selectEducators')
      }

      return this.editMode
        ? this.$t('bookingEdit.submitBtn')
        : this.$t('bookingCreate.submitBtn')
    },
    primaryLoading() {
      return (
        this.isLoadingCRUD ||
        this.isLoadingBookingBlocks ||
        this.isLoadingEditBooking
      )
    },
    primaryDisabled() {
      if (!this.isOnDetailsStep) return false

      const { isFormValid, formDirty } = this.createBookingV2Header
      const isSubmitAction = this.editMode && this.isSendToANZUK

      return isSubmitAction ? !formDirty || !isFormValid : !isFormValid
    },
    primaryTooltip() {
      if (!this.isOnDetailsStep) return ''

      return this.createBookingV2Header.submitButtonTooltip
    },
  },
  methods: {
    ...mapActions({
      setCreateBookingV2Step: 'bookings/setCreateBookingV2Step',
      requestCreateBookingV2Submit: 'bookings/requestCreateBookingV2Submit',
    }),
    handlePrimaryAction() {
      if (this.isOnDetailsStep && !this.isSendToANZUK) {
        this.setCreateBookingV2Step(CreateBookingSteps.SelectEducatorsStep)
        return
      }

      this.requestCreateBookingV2Submit()
    },
    backToDetails() {
      this.setCreateBookingV2Step(CreateBookingSteps.DetailsStep)
    },
  },
}
</script>

<template>
  <header
    class="white d-flex justify-space-between w-100 pa-6"
    data-testid="booking-create-header-v2"
  >
    <div>
      <v-btn
        class="mb-1 pl-0 pr-1 text-sm-bold neutrals-grey-500-text"
        elevation="0"
        text
        data-test-btn="close-action"
        @click="$router.go(-1)"
      >
        <v-icon size="20" class="mr-2">mdi-chevron-left</v-icon>
        <span>{{ $t('general.leave') }}</span>
      </v-btn>
      <h1 class="display-sm my-0">
        {{ pageTitle }}
      </h1>
    </div>
    <div class="d-flex align-center">
      <v-btn
        v-if="isOnSelectEducatorsStep"
        outlined
        class="text-md-bold booking-create-header-button border-grey-200 mr-2"
        @click="backToDetails"
        >{{ $t('bookingCreateV2.backToDetails') }}</v-btn
      >

      <v-tooltip bottom :disabled="!primaryDisabled || !primaryTooltip">
        <template v-slot:activator="{ on }">
          <span v-on="on">
            <v-btn
              class="text-md-bold booking-create-header-button"
              data-test-btn="primary-action"
              elevation="0"
              color="primary"
              :disabled="primaryDisabled"
              :loading="primaryLoading"
              @click="handlePrimaryAction"
            >
              <transition name="fade-cycle-down" mode="out-in">
                <span :key="primaryActionLabel">{{ primaryActionLabel }}</span>
              </transition>
              <v-icon right size="20" class="white--text"
                >mdi-chevron-right</v-icon
              >
            </v-btn>
          </span>
        </template>
        <span data-testid="booking-create-header__submit-btn-tooltip-text">{{
          primaryTooltip
        }}</span>
      </v-tooltip>
    </div>
  </header>
</template>

<style lang="scss" scoped>
@import '@/styles/_vue-transitions.scss';
@import '@/styles/_r2b-colours.scss';

.booking-create-header-button {
  min-width: 0 !important;
  height: 47px !important;
  padding-top: 10px !important;
  padding-bottom: 10px !important;
  letter-spacing: 0.15px;
  border-radius: 8px !important;
}
</style>
